import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useAureliaI18n } from '../../react-hooks/useAureliaI18n';
import { useClient } from '../../react-hooks/useClient';
import SioIcon from '../../icon/rsuite-icon-font/SioIcon';
import flattenMenu from './flattenMenu';

// Debounce before hitting the backend, and the shortest query we run model
// TODO: Need to change the debaounce ms and result per model
const SEARCH_DEBOUNCE_MS = 300;
const MIN_MODEL_QUERY = 2;
const RESULTS_PER_MODEL = 8;
const MAX_SUB_LINES = 2;


const RECENT_SEARCH_STORAGE_KEY = 'sio-recent-searches';
const RECENT_SEARCH_LIMIT = 8;

const readRecentSearches = () => {
  try {
    const raw = JSON.parse(
      localStorage.getItem(RECENT_SEARCH_STORAGE_KEY) || '[]'
    );
    return Array.isArray(raw)
      ? raw.filter((term) => typeof term === 'string' && term.trim())
      : [];
  } catch {
    return [];
  }
};

const writeRecentSearches = (list) => {
  try {
    localStorage.setItem(RECENT_SEARCH_STORAGE_KEY, JSON.stringify(list));
  } catch {
    // localStorage may be unavailable
  }
};


const pushRecentSearch = (term) => {
  const value = String(term ?? '').trim();
  if (!value) {
    return readRecentSearches();
  }
  const lower = value.toLowerCase();
  const next = [
    value,
    ...readRecentSearches().filter((t) => t.toLowerCase() !== lower),
  ].slice(0, RECENT_SEARCH_LIMIT);
  writeRecentSearches(next);
  return next;
};


const MENU_GROUP_ICON = 'fa fa-bars';

const moduleOf = (view) =>
  typeof view === 'string' && view.includes('/') ? view.split('/')[0] : null;

const buildMenuIconMaps = (menu) => {
  const byView = {};
  const byModule = {};

  const record = (view, icon) => {
    if (!view || !icon) {
      return;
    }
    if (!(view in byView)) {
      byView[view] = icon;
    }
    const mod = moduleOf(view);
    if (mod && !(mod in byModule)) {
      byModule[mod] = icon;
    }
  };

  if (!Array.isArray(menu)) {
    return { byView, byModule };
  }

  menu.forEach((item) => {
    if (item.user || item.name === 'user') {
      return;
    }
    const categoryIcon = item.icon || null;
    record(item.view, categoryIcon);

    if (!item.items) {
      return;
    }

    const subItems = [
      ...(item.items.noGroup || []),
      ...Object.values(item.items.groups || {}).flat(),
    ];
    subItems.forEach((sub) => record(sub.view, sub.icon || categoryIcon));
  });

  return { byView, byModule };
};

const resolveModelIcon = (maps, candidates) => {
  for (const view of candidates) {
    if (view && maps.byView[view]) {
      return maps.byView[view];
    }
  }
  for (const view of candidates) {
    const mod = moduleOf(view);
    if (mod && maps.byModule[mod]) {
      return maps.byModule[mod];
    }
  }
  return null;
};


const GLOBAL_SEARCH_OPEN_EVENT = 'sio:open-global-search';
export const openGlobalSearch = () =>
  document.dispatchEvent(new CustomEvent(GLOBAL_SEARCH_OPEN_EVENT));

const GlobalSearch = ({ menu }) => {
  const { t } = useAureliaI18n();
  const client = useClient();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const [searchableModels, setSearchableModels] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [modelGroups, setModelGroups] = useState([]);
  const [pending, setPending] = useState(0);

  const inputRef = useRef(null);
  const anchorRefs = useRef([]);
  const configFetchedRef = useRef(false);
  const searchGenRef = useRef(0);
  const menuItems = useMemo(
    () =>
      flattenMenu(menu).map((entry) => ({
        ...entry,
        labelText: t(entry.label),
        parentText: entry.parentLabel ? t(entry.parentLabel) : '',
        groupText: entry.group ? t(entry.group) : '',
      })),
    [menu, t]
  );

  const menuIconMaps = useMemo(() => buildMenuIconMaps(menu), [menu]);
  const iconForModel = useCallback(
    (model) => resolveModelIcon(menuIconMaps, [model.view, model.key]),
    [menuIconMaps]
  );

  const menuMatches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return [];
    }
    return menuItems.filter(
      (it) =>
        it.labelText.toLowerCase().includes(q) ||
        it.groupText.toLowerCase().includes(q) ||
        it.parentText.toLowerCase().includes(q)
    );
  }, [menuItems, query]);

  const modelOrder = useMemo(() => {
    const order = {};
    searchableModels.forEach((model, index) => {
      order[model.key] = index;
    });
    return order;
  }, [searchableModels]);

  useEffect(() => {
    if (!open || configFetchedRef.current || !client) {
      return;
    }
    configFetchedRef.current = true;
    client.get('global-search/config').then(
      (data) => setSearchableModels(data?.models ?? []),
      () => setSearchableModels([])
    );
  }, [open, client]);


  const runModelSearch = useCallback(
    (q) => {
      const generation = ++searchGenRef.current;
      setModelGroups([]);

      if (!client || searchableModels.length === 0) {
        setPending(0);
        return;
      }

      setPending(searchableModels.length);

      const conditions = encodeURIComponent(JSON.stringify({ search: q }));

      searchableModels.forEach((model) => {
        const url = `model/choice/${model.key}?conditions=${conditions}&limit=${RESULTS_PER_MODEL}`;

        const settle = () => {
          if (generation === searchGenRef.current) {
            setPending((count) => Math.max(0, count - 1));
          }
        };

        client.get(url).then((items) => {
          if (generation !== searchGenRef.current) {
            return; // a newer query has started; drop this result
          }

          const list = Array.isArray(items) ? items : [];

          if (list.length > 0) {
            setModelGroups((prev) =>
              [
                ...prev.filter((group) => group.key !== model.key),
                {
                  key: model.key,
                  label: model.label,
                  view: model.view,
                  items: list,
                },
              ].sort((a, b) => modelOrder[a.key] - modelOrder[b.key])
            );
          }

          settle();
        }, settle);
      });
    },
    [client, searchableModels, modelOrder]
  );

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const q = query.trim();

    if (q.length < MIN_MODEL_QUERY) {
      searchGenRef.current++; // invalidate any in-flight search
      setModelGroups([]);
      setPending(0);
      return undefined;
    }

    const handle = setTimeout(() => runModelSearch(q), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [open, query, runModelSearch]);


  const sections = useMemo(() => {
    const result = [];
    if (menuMatches.length > 0) {
      result.push({
        key: '__menu__',
        type: 'menu',
        title: t('sio.menu.global_search_menu_group'),
        items: menuMatches,
      });
    }
    modelGroups.forEach((group) =>
      result.push({ ...group, type: 'model', title: t(group.label) })
    );
    return result;
  }, [menuMatches, modelGroups, t]);

  const hrefFor = useCallback((section, item) => {
    if (section.type === 'menu') {
      return item.href;
    }
    const id = item.value?.id;
    if (!section.view || !id) {
      return null;
    }
    return `/view/${section.view}?id=${encodeURIComponent(id)}`;
  }, []);

  // Flatten every navigable row so arrow keys can move across groups.
  const flatRows = useMemo(() => {
    const rows = [];
    sections.forEach((section) => {
      section.items.forEach((item) => {
        const href = hrefFor(section, item);
        if (href) {
          rows.push({ section, item, href });
        }
      });
    });
    return rows;
  }, [sections, hrefFor]);

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setActiveIndex(0);
    setModelGroups([]);
    setPending(0);
  }, []);

  const recordRecentSearch = useCallback((term) => {
    setRecentSearches(pushRecentSearch(term));
  }, []);

  // Global open shortcut: Cmd/Ctrl + K (toggles).
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    document.addEventListener(GLOBAL_SEARCH_OPEN_EVENT, onOpen);
    return () => document.removeEventListener(GLOBAL_SEARCH_OPEN_EVENT, onOpen);
  }, []);

  useEffect(() => {
    if (open) {
      setRecentSearches(readRecentSearches());
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [open]);


  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const { body } = document;
    const previousOverflow = body.style.overflow;
    const previousPaddingRight = body.style.paddingRight;
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      const current =
        parseFloat(window.getComputedStyle(body).paddingRight) || 0;
      body.style.paddingRight = `${current + scrollbarWidth}px`;
    }
    return () => {
      body.style.overflow = previousOverflow;
      body.style.paddingRight = previousPaddingRight;
    };
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, flatRows.length]);

  useEffect(() => {
    const node = anchorRefs.current[activeIndex];
    if (node && node.scrollIntoView) {
      node.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  if (!open) {
    return null;
  }

  const onInputKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (flatRows.length ? (i + 1) % flatRows.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) =>
        flatRows.length ? (i - 1 + flatRows.length) % flatRows.length : 0
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      recordRecentSearch(query);
      const node = anchorRefs.current[activeIndex];
      if (node) {
        node.click();
        close();
      }
    }
  };

  const applyRecentSearch = (term) => {
    setQuery(term);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const clearRecentSearches = () => {
    writeRecentSearches([]);
    setRecentSearches([]);
  };

  anchorRefs.current.length = flatRows.length;

  const labelFor = (section, item) =>
    section.type === 'menu'
      ? item.labelText
      : item.label ?? item.renderLabel ?? '';


  const subLabelFor = (section, item) => {
    if (section.type === 'menu') {
      const trail = [item.parentText, item.groupText]
        .filter(Boolean)
        .join(' › ');
      return trail ? [trail] : [];
    }
    const help = item.help ?? item.renderSubLabel ?? '';
    return String(help)
      .split(/\r\n|\r|\n/)
      .map((line) => line.replace(/\s+/g, ' ').trim())
      .filter(Boolean);
  };

  const hasResults = flatRows.length > 0;
  const showInfo = !hasResults && pending === 0;

  let rowIndex = -1;

  return createPortal(
    <div className="menu-spotlight-overlay" onMouseDown={close}>
      <div
        className="menu-spotlight"
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="menu-spotlight-search">
          <SioIcon icon="fa fa-search" />
          <input
            ref={inputRef}
            value={query}
            placeholder={t('sio.menu.global_search_placeholder')}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKeyDown}
          />
          {pending > 0 ? (
            <SioIcon icon="fa fa-spinner fa-pulse" className="menu-spotlight-loading" />
          ) : (
            <span className="menu-spotlight-hint" onClick={close}>
              esc
            </span>
          )}
        </div>

        <ul className="menu-spotlight-results">
          {sections.map((section) => {
            const groupIcon =
              section.type === 'menu' ? MENU_GROUP_ICON : iconForModel(section);
            return (
            <React.Fragment key={section.key}>
              <li className="menu-spotlight-group">
                {groupIcon && (
                  <SioIcon
                    icon={groupIcon}
                    className="menu-spotlight-group-icon"
                  />
                )}
                {section.title}
              </li>
              {section.items.map((item, i) => {
                const href = hrefFor(section, item);
                if (!href) {
                  return null;
                }
                rowIndex += 1;
                const index = rowIndex;
                const subLines = subLabelFor(section, item);
                return (
                  <li
                    key={`${section.key}-${item.value?.id ?? item.href ?? i}`}
                    className={`menu-spotlight-item ${
                      index === activeIndex ? 'active' : ''
                    }`}
                  >
                    <a
                      ref={(el) => (anchorRefs.current[index] = el)}
                      href={href}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => {
                        recordRecentSearch(query);
                        close();
                      }}
                    >
                      <span className="menu-spotlight-item-label">
                        {labelFor(section, item)}
                      </span>
                      {subLines.length > 0 && (
                        <span className="menu-spotlight-item-sub">
                          {subLines.slice(0, MAX_SUB_LINES).map((line, li) => (
                            <span
                              key={li}
                              className="menu-spotlight-item-sub-line"
                            >
                              {line}
                            </span>
                          ))}
                        </span>
                      )}
                    </a>
                  </li>
                );
              })}
            </React.Fragment>
            );
          })}

          {showInfo && (
            <li className="menu-spotlight-info">
              {query.trim() && (
                <div className="menu-spotlight-info-empty">
                  {t('sio.menu.global_search_no_results')}
                </div>
              )}
              {recentSearches.length > 0 && !query.trim() && (
                <div className="menu-spotlight-info-recent">
                  <div className="menu-spotlight-info-recent-header">
                    <span className="menu-spotlight-info-recent-title">
                      <SioIcon icon="fa fa-history" />
                      {t('sio.menu.global_search_recent_group')}
                    </span>
                    <button
                      type="button"
                      className="menu-spotlight-info-recent-clear"
                      onClick={clearRecentSearches}
                    >
                      {t('sio.menu.global_search_recent_clear')}
                    </button>
                  </div>
                  <div className="menu-spotlight-info-tags">
                    {recentSearches.map((term) => (
                      <button
                        key={term}
                        type="button"
                        title={term}
                        className="menu-spotlight-info-tag menu-spotlight-info-tag--recent"
                        onClick={() => applyRecentSearch(term)}
                      >
                        <span className="menu-spotlight-info-tag-text">
                          {term}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="menu-spotlight-info-title">
                {t('sio.menu.global_search_searchable_hint')}
              </div>
              <div className="menu-spotlight-info-model">
                <div className="menu-spotlight-info-model-label">
                  <SioIcon
                    icon={MENU_GROUP_ICON}
                    className="menu-spotlight-info-model-icon"
                  />
                  {t('sio.menu.global_search_menu_heading')}
                </div>
                <div className="menu-spotlight-info-tags">
                  <span className="menu-spotlight-info-tag">
                    {t('sio.menu.global_search_menu_field')}
                  </span>
                </div>
              </div>
              {searchableModels.map((model) => {
                const modelIcon = iconForModel(model);
                return (
                <div key={model.key} className="menu-spotlight-info-model">
                  <div className="menu-spotlight-info-model-label">
                    {modelIcon && (
                      <SioIcon
                        icon={modelIcon}
                        className="menu-spotlight-info-model-icon"
                      />
                    )}
                    {t(model.label)}
                  </div>
                  {model.fields?.length > 0 && (
                    <div className="menu-spotlight-info-tags">
                      {model.fields.map((f) => (
                        <span key={f} className="menu-spotlight-info-tag">
                          {t(f)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                );
              })}

              <div className="menu-spotlight-info-footer">
                {t('sio.menu.global_search_add_models_hint')}
              </div>
            </li>
          )}
        </ul>
      </div>
    </div>,
    document.body
  );
};

export default GlobalSearch;
